import { VelavuFloor, VelavuSite } from "velavu-js-api";
import {
	MapboxOverlayBox,
	MapHelperInstance,
	useStyleDependantMapEffect,
} from "../../helper/map-helper";
import { getFloorTarget } from "../../helper/floor-helper";
import { imageSourceBlank } from "./mapbox-injectable";
import { useMemo } from "react";

const mapboxIDSiteFloorplanPrefix: string = "site-floorplan-";
const mapboxIDSiteFloorplanImagePrefix: string = "site-floorplan-image-";

export interface MapboxInjectableSiteFloorplansProps {
	sites?: VelavuSite[] | undefined;
	siteCounts?: number[] | undefined;
	floorOverride?: { [key: string]: string | undefined };
	editingSiteId?: string;
	belowLayer?: string;
}

/**
 * An interface that contains the necessary information to render a site floor
 */
interface FloorRenderable {
	siteID: string;
	floor: VelavuFloor;
}

function drawFloorplans(
	box: MapboxOverlayBox,
	renderables: FloorRenderable[],
	editingSiteId: string | undefined,
	belowLayer?: string | undefined,
): string[] {
	const createdIDs: string[] = [];

	renderables.forEach(({ siteID, floor }) => {
		const mapboxID = mapboxIDSiteFloorplanPrefix + floor.id;
		createdIDs.push(mapboxID);

		box.addSource(mapboxID, {
			type: "geojson",
			data: floor.floorplan,
		});

		box.addLayer(
			{
				id: mapboxID,
				source: mapboxID,
				type: "fill-extrusion",
				paint: {
					"fill-extrusion-color": "#5F718C",
					"fill-extrusion-height": 3.5,

					"fill-extrusion-ambient-occlusion-intensity": 0.7,
					"fill-extrusion-opacity":
						siteID !== editingSiteId ? 0.6 : 0,
				},
			},
			belowLayer,
		);
		belowLayer = mapboxID;
	});

	return createdIDs;
}

function drawFloorplanImages(
	box: MapboxOverlayBox,
	renderables: FloorRenderable[],
	editingSiteId: string | undefined,
	belowLayer?: string | undefined,
): string[] {
	const createdIDs: string[] = [];

	renderables.forEach(({ siteID, floor }) => {
		const mapboxID = mapboxIDSiteFloorplanImagePrefix + floor.id;
		createdIDs.push(mapboxID);

		let floorImageSource = { ...imageSourceBlank };
		if (siteID !== editingSiteId) {
			if (floor.image && floor.image_coordinates) {
				floorImageSource = {
					url: floor.image,
					coordinates: floor.image_coordinates,
				};
			}
		}

		box.addSource(mapboxID, {
			type: "image",
			...floorImageSource,
		});

		box.addLayer(
			{
				id: mapboxID,
				source: mapboxID,
				type: "raster",
				paint: {
					"raster-fade-duration": 0,
					"raster-opacity": 0.8,
				},
			},
			belowLayer,
		);

		belowLayer = mapboxID;
	});

	return createdIDs;
}

export default function useMapboxInjectableSiteFloorplans(
	mapInstance: MapHelperInstance,
	props: MapboxInjectableSiteFloorplansProps,
): string | undefined {
	const allSites = props.sites;
	const floorOverride = props.floorOverride;

	//Resolve the floor to show for each site
	const resolvedFloorSites = useMemo(() => {
		if (allSites === undefined) {
			return [];
		}

		//Filter out sites with no floors
		return allSites
			.map((site): FloorRenderable | null => {
				//Ignore sites with no floors
				if (site.floors === undefined || site.floors.length === 0) {
					return null;
				}

				//Resolve the floor to use
				const resolvedFloor = getFloorTarget(
					site.floors!,
					floorOverride?.[site.id],
				)!;
				return {
					siteID: site.id,
					floor: resolvedFloor,
				};
			})
			.filter((it) => it !== null);
	}, [allSites, floorOverride]);

	//Hash resolvedFloorSites to skip re-rendering if nothing actually changed
	const resolvedFloorSitesHash = useMemo(() => {
		return JSON.stringify(resolvedFloorSites);
	}, [resolvedFloorSites]);

	useStyleDependantMapEffect(
		mapInstance,
		(_, box) => {
			if (resolvedFloorSites.length === 0) {
				return;
			}

			const floorplanLayerIDs = drawFloorplans(
				box,
				resolvedFloorSites,
				props.editingSiteId,
				props.belowLayer,
			);

			drawFloorplanImages(
				box,
				resolvedFloorSites,
				props.editingSiteId,
				floorplanLayerIDs[floorplanLayerIDs.length - 1],
			);
		},
		[resolvedFloorSitesHash, props.editingSiteId, props.belowLayer],
	);

	//Return the layer ID of the last site
	if (allSites !== undefined && allSites.length !== 0) {
		return (
			mapboxIDSiteFloorplanImagePrefix + allSites[allSites.length - 1].id
		);
	} else {
		return undefined;
	}
}
